import React, { useState } from 'react';
import { variant, match, fields } from 'variant';
import type { VariantOf } from 'variant';
import { getRepoType, createPackageManager } from '@wix/package-manager';
import { Box, ConfirmInput, Spinner, Text } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useProjectModel } from '../../model.js';
import { useWixCliAppBi } from '../../bi/index.js';

interface Props {
  onFinish: () => void;
  onSubmit?: () => void;
}

const InstallationStateVariant = variant({
  NotStarted: {},
  Skipped: fields<{ pkgManagerName: string }>(),
  Installing: {},
  Failure: {},
  Success: {},
});

type InstallationState = VariantOf<typeof InstallationStateVariant>;

export const InstallDependencies: React.FC<Props> = ({
  onFinish,
  onSubmit,
}) => {
  const { model } = useProjectModel();
  const { t } = useTranslation();
  const bi = useWixCliAppBi();

  const [installState, setInstallState] = useState<InstallationState>(
    InstallationStateVariant.NotStarted()
  );

  return (
    <>
      <Box marginBottom={1}>
        <ConfirmInput
          label={t('generate_command.generation.install_dependencies_question')}
          initialValue={true}
          onSubmit={async (confirmed) => {
            bi.cliFlowStepAnswered({
              question: t(
                'generate_command.generation.install_dependencies_question'
              ),
              questionKey:
                'generate_command.generation.install_dependencies_question',
              answer: String(confirmed),
            });

            const { projectFolder } = model;
            const repoType = await getRepoType(projectFolder);
            const pkgManager = await createPackageManager(repoType);

            onSubmit?.();

            if (confirmed) {
              setInstallState(InstallationStateVariant.Installing());
              try {
                await pkgManager.runInstall(projectFolder);
              } catch (e) {
                setInstallState(InstallationStateVariant.Failure());
                throw e;
              }
              setInstallState(InstallationStateVariant.Success());
            } else {
              setInstallState(
                InstallationStateVariant.Skipped({
                  pkgManagerName: pkgManager.name,
                })
              );
            }

            onFinish();
          }}
        />
      </Box>
      {match(installState, {
        NotStarted: () => null,
        Failure: () => null,
        Skipped: ({ pkgManagerName }) => (
          <Box flexDirection="column">
            <Text skin="warning">
              <Trans
                i18nKey="generate_command.generation.new_dependencies"
                values={{ pkgManagerName }}
                components={[<Text skin="info" />]}
              />
            </Text>
          </Box>
        ),
        Installing: () => <Spinner text={t('general.loading')} />,
        Success: () => (
          <Box flexDirection="column">
            <Trans i18nKey="generate_command.generation.dependencies_installed" />
          </Box>
        ),
      })}
    </>
  );
};
